import { Injectable } from '@angular/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override parse = (value: unknown): Date | null => {
    if (typeof value === 'string') {
      let day, month, year;

      if (value.match(/^\d{1,2}\.\d{1,2}\.\d{4}$/)) {
        const [dayStr, monthStr, yearStr] = value.split('.');
        day = Number(dayStr);
        month = Number(monthStr);
        year = Number(yearStr);
      } else if (value.match(/^\d{8}$/)) {
        day = Number(value.substring(0, 2));
        month = Number(value.substring(2, 4));
        year = Number(value.substring(4, 8));
      } else {
        return null;
      }

      if (!isValidDate(day, month, year)) {
        return null;
      }

      return new Date(year, month - 1, day);
    }
    return null;
  };

  override format = (date: Date, displayFormat: object): string => {
    if (date) {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    }
    return super.format(date, displayFormat);
  };
}

export const customDateAdapter = { provide: DateAdapter, useClass: CustomDateAdapter };
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const isValidDate = (day: number, month: number, year: number): boolean => {
  if (month < 1 || month > 12 || day < 1 || year.toString().length !== 4) {
    return false;
  }

  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const monthLengths = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= monthLengths[month - 1];
};

export const getFirstOfTheNextMonth = (): Date => {
  const heute = new Date();
  const ersterTagNaechsterMonat = new Date(heute.getFullYear(), heute.getMonth() + 1, 1);
  return ersterTagNaechsterMonat;
};

/** Midnight tomorrow — for the common "earliest the day after today" rule on a Vertragsbeginn. */
export const getTomorrowDate = (): Date => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
};

export const getFirstDateOfYear = (year: number) => {
  const firstDateOfYear = new Date(year, 0, 1);
  return firstDateOfYear;
};

export const getFirstOfTheNextMonthMinusThreeMonths = (): Date => {
  const today = new Date();
  const firstDayNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const firstDayNextMonthMinusThreeMonths = new Date(
    firstDayNextMonth.getFullYear(),
    firstDayNextMonth.getMonth() - 3,
    1
  );
  return firstDayNextMonthMinusThreeMonths;
};

export const getXYearInTheFutureDate = (numberOfYears: number): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + numberOfYears);
  return date;
};

export const getXYearInThePastDate = (numberOfYears: number): Date => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - numberOfYears);
  return date;
};

export const validateMinAge = (birthdateInput: string, minAge: number): boolean => {
  const birthdate = new Date(birthdateInput);
  const today = new Date();
  let age = today.getFullYear() - birthdate.getFullYear();
  const monthDiff = today.getMonth() - birthdate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthdate.getDate())) {
    age--;
  }
  return age >= minAge;
};

export const isSunday = (date: Date | null): boolean => {
  if (!date) return false;
  return date.getDay() !== 0;
};

// https://stackoverflow.com/questions/17415579/how-to-iso-8601-format-a-date-with-timezone-offset-in-javascript
export const toIsoString = (date: Date) => {
  const tzo = -date.getTimezoneOffset(),
    dif = tzo >= 0 ? '+' : '-',
    pad = function (num: number) {
      return (num < 10 ? '0' : '') + num;
    };

  return (
    date.getFullYear() +
    '-' +
    pad(date.getMonth() + 1) +
    '-' +
    pad(date.getDate()) +
    'T' +
    pad(date.getHours()) +
    ':' +
    pad(date.getMinutes()) +
    ':' +
    pad(date.getSeconds()) +
    dif +
    pad(Math.floor(Math.abs(tzo) / 60)) +
    ':' +
    pad(Math.abs(tzo) % 60)
  );
};
